import { useEffect, useState } from 'react';

import PubSub from 'pubsub-js';

import { createContainer } from '@fuxi/eevee-hooks';

import { MediaType, MonorepoType } from '@yaotai/common-utils';
import {
  ActivityFeatureSwitch,
  DEFAULT_MAIN_LOGO,
  AntiSpamSwitchType,
  IdentityEnum,
  SwitchGroupEnum,
  SwitchType,
} from '../../constants';
import {
  SpaceCheckOptions,
  useAntiSpamFeatureSwitch,
  useCommonUrls,
  useDingZhi,
  useSpaceCheck,
  useBulkFeatureSwitch,
  useAllAccountInfo,
} from '../../hooks';
import { ACTIVITY_AUTH_IDENTITY_TOKEN, UN_WARP_ACTIVITY_TOKEN } from './helper';

export type GlobalContextContainerOptions = {
  isMainVersion?: boolean;
  mediaType?: MediaType;
  monorepoType?: MonorepoType;
  spaceAuthOptions?: Pick<SpaceCheckOptions, 'isDingzhiWithSpace'>;
};

const _useYaotaiGlobalContextContainer = (options: GlobalContextContainerOptions = {}) => {
  // 用于写在:aid 外层的组件通过 PubSub 的模式获取到当前页面的 aid
  const [currentAid, setCurrentAid] = useState('');

  const [currentActivityIdentity, setCurrentActivityIdentity] = useState(0);
  const { spaceAuthOptions, monorepoType, isMainVersion, mediaType } = options;
  const commonUrls = useCommonUrls();
  const dingZhiState = useDingZhi({ isMainVersion });

  const spaceAuth = useSpaceCheck({ ...(spaceAuthOptions || {}), monorepoType, isMainVersion });

  const { isGettingAllAccountsInfo, allAccountInfo, refetchAllAccountInfo } = useAllAccountInfo({ monorepoType });

  const { bulkSwitchConf, bulkLoading } = useBulkFeatureSwitch({
    aid: currentAid,
    switchGroups: [SwitchGroupEnum.Func, SwitchGroupEnum.Oem, SwitchGroupEnum.Tourist],
    monorepoType,
  });

  // 原来的开关逻辑（场地更新、添加场地、更新短信签名...）
  const switchConf = bulkSwitchConf?.['FUNC'] || {};
  const oem = bulkSwitchConf?.['ACTIVITY_OEM'] || {};
  const tourist = bulkSwitchConf?.['TICKET'] || {};
  const hasAddVenueSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.ADD_VENUE]?.status;
  const hasUpdateBasicInfoSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.BASIC_INFO_UPDATE]?.status;
  const hasMsgSignSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.MSG_SIGN]?.status;
  const hasWeimeiSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.WEIMEI_CONFIG]?.status;
  const hasLiveConfigSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.LIVE_CONFIG]?.status;
  const hasStatisticSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.STATISTIC]?.status;
  const hasActivityDataSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.ACTIVITY_DATA]?.status;
  const hasNpcIntellDialogSwitchAuth = !!switchConf?.[SwitchType.NPC_INTELL_DIALOG]?.status;
  const hasNpcTextIntroSwitchAuth = !!switchConf?.[SwitchType.NPC_TEXT_INTRO]?.status;
  const hasNpcVoiceBoardSwitchAuth = !!switchConf?.[SwitchType.NPC_VOICE_BROAD]?.status;
  const hasPropsMallSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.PROP_MALL]?.status;
  const hasPrizeMallSwitchAuth = !!switchConf?.[ActivityFeatureSwitch.PRIZE_MALL]?.status;
  // 反垃圾开关逻辑
  const {
    switchConf: antiSpamSwitchConf,
    isGettingSwitchConfig: isGettingAntiSpamSwitchConfig,
    refetchAntiSampleStatus,
  } = useAntiSpamFeatureSwitch({ aid: currentAid, monorepoType });
  const hasAntiSpamPlayerNameAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_PLAYER_NAME]?.status;
  const hasAntiSpamPlayerCustomTitleAuth =
    !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_PLAYER_CUSTOM_TITLE]?.status;
  const hasAntiSpamPlayerInfoAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_PLAYER_INFO]?.status;
  const hasAntiSpamDanmakuAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_DANMAKU]?.status;
  const hasAntiSpamActivityNameInfoAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_ACTIVITY_NAME]?.status;
  const hasAntiSpamNoticeInfoAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_NOTICE]?.status;

  const hasAntiSpamBoardAssetAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_DISPLAY_BOARD_ASSET]?.status;
  const hasAntiSpamVenueImageAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_VENUE_IMAGE]?.status;
  const hasAntiSpamLoginImageAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_LOGIN_IMAGE]?.status;

  const hasAntiSpamPlayerImageAuth = !!antiSpamSwitchConf[AntiSpamSwitchType.ANTISPAM_PLAYER_IMAGE]?.status;
  // 分为两种情况 1：有图且不是默认图 2：关闭了主 LOGO 选项
  const isMainLogoOemCustom =
    (!!oem?.oem_log_lt_logo?.detail && oem?.oem_log_lt_logo?.detail !== DEFAULT_MAIN_LOGO) ||
    oem?.oem_log_lt_logo?.status === false;

  useEffect(() => {
    const aid_token = PubSub.subscribe(UN_WARP_ACTIVITY_TOKEN, (_: string, payload: string) => setCurrentAid(payload));
    return () => {
      PubSub.unsubscribe(aid_token);
    };
  }, []);

  useEffect(() => {
    const activity_identity_token = PubSub.subscribe(ACTIVITY_AUTH_IDENTITY_TOKEN, (_: string, payload: number) =>
      setCurrentActivityIdentity(payload),
    );
    return () => {
      PubSub.unsubscribe(activity_identity_token);
    };
  }, []);

  useEffect(() => {
    if (!currentAid) return;
    if (!['reception'].includes(monorepoType || '')) return;
    if ([IdentityEnum.SYSTEM, IdentityEnum.ORGANIZER].includes(currentActivityIdentity)) {
      refetchAntiSampleStatus(currentAid); // 只有系统支持和组织者可以调用反垃圾信息接口
    }
  }, [currentActivityIdentity, currentAid, monorepoType, refetchAntiSampleStatus]);

  return {
    commonUrls,
    isDemoPage: commonUrls.demoAid === currentAid,
    spaceAuth,
    oem,
    tourist,
    currentAid,
    setCurrentAid,
    setCurrentActivityIdentity,
    refetchAntiSampleStatus,
    monorepoType,
    mediaType,
    allAccountInfo,
    refetchAllAccountInfo,
    // 反垃圾开关状态
    antiSpamSwitchStatus: {
      hasAntiSpamPlayerNameAuth,
      hasAntiSpamPlayerCustomTitleAuth,
      hasAntiSpamPlayerInfoAuth,
      hasAntiSpamNoticeInfoAuth,
      hasAntiSpamBoardAssetAuth,
      hasAntiSpamVenueImageAuth,
      hasAntiSpamLoginImageAuth,
      hasAntiSpamPlayerImageAuth,
      hasAntiSpamActivityNameInfoAuth,
      hasAntiSpamDanmakuAuth,
    },
    // 非 oem 的开关逻辑
    switchStatus: {
      hasAddVenueSwitchAuth,
      hasUpdateBasicInfoSwitchAuth,
      hasMsgSignSwitchAuth,
      hasWeimeiSwitchAuth,
      hasLiveConfigSwitchAuth,
      hasStatisticSwitchAuth,
      hasActivityDataSwitchAuth,
      hasNpcIntellDialogSwitchAuth,
      hasNpcTextIntroSwitchAuth,
      hasNpcVoiceBoardSwitchAuth,
      hasPropsMallSwitchAuth,
      hasPrizeMallSwitchAuth,
    },
    isMainLogoOemCustom,
    dingZhiState,
    isGlobalLoading: [
      commonUrls.isGettingCommonUrls,
      dingZhiState.isGettingDingZhiConfig,
      spaceAuth.isCheckingSpacePermission,
      bulkLoading,
      isGettingAntiSpamSwitchConfig,
      isGettingAllAccountsInfo,
    ].some(item => item),
    loadings: {
      isGettingCommonUrls: commonUrls.isGettingCommonUrls,
      isGettingDingZhiConfig: dingZhiState.isGettingDingZhiConfig,
      isCheckingSpacePermission: spaceAuth.isCheckingSpacePermission,
      isGettingSwitchConfig: bulkLoading,
      isGettingAntiSpamSwitchConfig,
      isGettingAllAccountsInfo,
    },
  };
};

export const YaotaiGlobalContext = createContainer(_useYaotaiGlobalContextContainer);
export const useYaotaiGlobalContextState = YaotaiGlobalContext.useContainer;
export * from './helper';
export * from './hooks';
